import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import { useAuth } from "../Common/Configurations/AuthContext";
import ToolsSection from "../StaticPages/ToolsSection";
import SuppliersSection from "../StaticPages/SuppliersSection";
import DefaultData from "../CompanyData/DefaultData";
import LoginScreenLeftSide from "./LoginScreenLeftSide";
import PortalSetUpSteps from "../StaticPages/PortalSetUpSteps";
import "./GlobalSupplier.css";

// Constants
const initialLoginValues = { username: "", password: "" };
const ALLOWED_TENANTS = ["localhost", "trawtel", "zotrai"];
const ERROR_MESSAGES = {
  USERNAME_REQUIRED: "Please enter your username.",
  PASSWORD_REQUIRED: "Please enter your password.",
  LOGIN_FAILED: "Login failed. Please try again.",
  NETWORK_ERROR: "Network error. Please check your connection.",
};

// Helper: Extract and validate tenant from hostname
const getTenantFromHostname = () => {
  let host = window.location.hostname;
  // Remove common prefixes
  host = host.replace(/^(www\.|admin\.|app\.)/, "");
  return host.split(".")[0];
};

// Helper: Check if tenant should show registration
const shouldShowRegistration = (tenant) => ALLOWED_TENANTS.includes(tenant);
export default function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState(initialLoginValues);
  const [errors, setErrors] = useState({});
  const [btnSubmit, setBtnSubmit] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error when user starts typing
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
    // Clear submit error when user interacts
    if (errors.submit) setErrors((prev) => ({ ...prev, submit: "" }));
  }, [errors]);

  const validate = useCallback(() => {
    const validationErrors = {};
    
    if (!values.username?.trim()) {
      validationErrors.username = ERROR_MESSAGES.USERNAME_REQUIRED;
    }
    if (!values.password?.trim()) {
      validationErrors.password = ERROR_MESSAGES.PASSWORD_REQUIRED;
    }
    
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [values]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const resetForm = useCallback(() => {
    setValues(initialLoginValues);
    setErrors({});
  }, []);

  // Initialize tenant on mount
  useEffect(() => {
    const tenant = getTenantFromHostname();
    setShowRegistration(shouldShowRegistration(tenant));

    const tenantToStore = tenant === "localhost" ? DefaultData.TENANTID : tenant;
    localStorage.setItem("tenant", tenantToStore);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setBtnSubmit(true);
    const formData = {
      username: values.username.trim(),
      password: values.password,
      tenant: localStorage.getItem("tenant"),
      companyId:  DefaultData.DEFAULTCOMPANYID
    };

    try {
      const result = await apiService.post(config.LOGIN, formData);
      
      if (result.registrationPending) {
        resetForm();
        navigate("/registration-pending", {
          state: {
            userEmail: result.userEmail || values.username,
            message: result.message,
          },
        });
        return;
      }

      // Successful login
      resetForm();
      login({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = ERROR_MESSAGES.LOGIN_FAILED;

      // Handle specific error types
      if (error.message === "Network Error") {
        errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }

      setErrors((prev) => ({
        ...prev,
        submit: errorMessage,
      }));
    } finally {
      setBtnSubmit(false);
    }
  }, [validate, values.username, values.password, login, navigate, resetForm]);

  // Show loading spinner while AuthContext is initializing
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="globalsupplier-main-wrapper">
        <section
          className="globalsupplier-hero-section"
          style={{
            background: `linear-gradient(135deg, ${DefaultData.THEMECOLOR1} 0%, #0f172a 100%)`,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7 mb-5 mb-lg-0 order-2 order-lg-1">
              <LoginScreenLeftSide />
            </div>
            <div className="col-lg-5 d-flex justify-content-lg-end order-1 order-lg-2 mb-4 mb-lg-0">
              <div className="w-100" style={{ maxWidth: "480px" }}>
                <div
                  className="card shadow-lg border-0"
                  style={{
                    borderRadius: "1rem",
                    width: "100%",
                    padding: "20px"
                  }}
                >
                  <div className="card-body p-lg-5 p-4">
                    <div className="text-center">
                      <img
                        src="/assets/images/Zotrai.png"
                        alt={`${DefaultData.DEFAULTBRANDNAME} Logo`}
                        style={{ height: "35px" }}
                        className="mb-3"
                      loading="lazy" />
                    </div>
                    <p className="text-muted text-center mb-4">
                      Sign in to continue...
                    </p>
                    {errors.submit && (
                      <div className="alert alert-danger">{errors.submit}</div>
                    )}
                    <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                      <div className="mb-3">
                        <label htmlFor="username" className="form-label">
                          Username
                        </label>
                        <input
                          className={`form-control ${errors.username ? "is-invalid" : ""}`}
                          name="username"
                          type="text"
                          value={values.username}
                          onChange={handleInputChange}
                          placeholder="Enter your username"
                        />
                        {errors.username && (
                          <div className="invalid-feedback d-block">
                            {errors.username}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            Password
                          </label>
                        </div>
                        <div className="position-relative">
                          <input
                            className={`form-control pe-5 ${errors.password ? "is-invalid" : ""}`}
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={values.password}
                            onChange={handleInputChange}
                            placeholder="Enter your password"
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none text-muted"
                            onClick={togglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            style={{ right: "0.5rem" }}
                          >
                            <i
                              className={`ri-eye-${showPassword ? "line" : "off-line"} fs-5`}
                              aria-hidden="true"
                            ></i>
                          </button>
                        </div>
                        {errors.password && (
                          <div className="invalid-feedback d-block">
                            {errors.password}
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <button
                          type="submit"
                          className="btn w-100"
                          style={{
                            backgroundColor: DefaultData.THEMECOLOR1,
                            color: DefaultData.THEMECOLOR3,
                          }}
                          disabled={btnSubmit}
                        >
                          {btnSubmit ? "Signing In..." : "Sign In"}
                        </button>
                      </div>
                      {showRegistration && (
                        <div className="mt-4 text-center">
                          <p className="mb-0 text-muted">
                            Don't have an account?{" "}
                            <Link
                              to="/register"
                              className="fw-semibold text-decoration-underline me-3"
                              style={{ color: DefaultData.THEMECOLOR2 }}
                            >
                              Register
                            </Link>
                            <br />
                            <Link
                              to="/forgot-password"
                              className="small text-decoration-underline"
                              style={{ color: DefaultData.THEMECOLOR2 }}
                            >
                              Forgot password?
                            </Link>
                          </p>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </section>
      </div>
    
      <PortalSetUpSteps />
      <ToolsSection />
      <SuppliersSection />
    </div>
  );
}